import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Sparkles, Trash2, Zap, CalendarCheck, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import BadgeCard from "@/components/BadgeCard";
import { ReminderSettings } from "@/components/ReminderSettings";
import { PayPalAdminCard } from "@/components/PayPalAdminCard";

export default function ProfilePage() {
  const { user, logout, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", height_cm: "", starting_weight_kg: "", goal_weight_kg: "" });
  const [entries, setEntries] = useState([]);
  const [aiPrompt, setAiPrompt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [achievements, setAchievements] = useState(null);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      height_cm: user.height_cm || "",
      starting_weight_kg: user.starting_weight_kg || "",
      goal_weight_kg: user.goal_weight_kg || "",
    });
    api.get("/journal").then((r) => setEntries(r.data.entries));
    api.get("/progress/achievements").then((r) => setAchievements(r.data));
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    await api.patch("/auth/profile", {
      name: form.name,
      height_cm: parseFloat(form.height_cm) || null,
      starting_weight_kg: parseFloat(form.starting_weight_kg) || null,
      goal_weight_kg: parseFloat(form.goal_weight_kg) || null,
    });
    await refresh();
    alert("Saved.");
  };

  const getPrompt = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/prompt");
      setAiPrompt(data.prompt);
    } finally { setLoading(false); }
  };

  const delJournal = async (id) => {
    await api.delete(`/journal/${id}`);
    setEntries((e) => e.filter((x) => x.id !== id));
  };

  return (
    <div className="fade-up">
      <p className="eyebrow">Profile</p>
      <h1 className="font-display font-black text-3xl md:text-5xl mt-2 brand-underline">
        THE BUILDER
      </h1>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <form onSubmit={save} className="card space-y-4" data-testid="profile-form">
          <div>
            <label className="label">Name</label>
            <input className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Height (cm)</label>
              <input className="input" type="number" value={form.height_cm} onChange={(e) => setForm({...form, height_cm: e.target.value})}/>
            </div>
            <div>
              <label className="label">Start (kg)</label>
              <input className="input" type="number" step="0.1" value={form.starting_weight_kg} onChange={(e) => setForm({...form, starting_weight_kg: e.target.value})}/>
            </div>
            <div>
              <label className="label">Goal (kg)</label>
              <input className="input" type="number" step="0.1" value={form.goal_weight_kg} onChange={(e) => setForm({...form, goal_weight_kg: e.target.value})}/>
            </div>
          </div>
          <button className="btn-primary" data-testid="save-profile">Save</button>
        </form>

        <div className="card">
          <p className="eyebrow">Your account</p>
          <p className="font-display font-bold text-lg mt-2 uppercase">{user?.name}</p>
          <p className="text-sm text-zinc-500 mt-1">{user?.email}</p>
          <p className="text-xs text-zinc-500 mt-3 uppercase tracking-widest">Started: {user?.program_start_date}</p>
          <button className="btn-secondary mt-6 flex items-center gap-2" onClick={logout} data-testid="logout-btn">
            <LogOut size={14}/> Sign out
          </button>
        </div>
      </div>

      <Link to="/membership" className="card card-interactive flex items-center justify-between mt-6" data-testid="profile-membership-link" style={user?.is_member ? { borderColor: "#FF0055" } : {}}>
        <div className="flex items-center gap-3">
          <CreditCard size={18} color="#FF0055" />
          <div>
            <p className="eyebrow" style={{ color: "#FF0055" }}>Membership</p>
            <p className="font-display font-bold uppercase mt-0.5" data-testid="profile-membership-status">
              {user?.role === "admin" ? "Admin · full access" : user?.is_member ? `Member · ${user?.membership?.status}` : "Not a member yet"}
            </p>
          </div>
        </div>
        <span className="text-zinc-500 text-sm">{user?.is_member ? "Manage →" : "Join →"}</span>
      </Link>

      <Link to="/checkin" className="card card-interactive flex items-center justify-between mt-6" data-testid="profile-checkin-link">
        <div className="flex items-center gap-3">
          <CalendarCheck size={18} color="#FF0055" />
          <div>
            <p className="eyebrow" style={{ color: "#FF0055" }}>Weekly</p>
            <p className="font-display font-bold uppercase mt-0.5">Sunday check-in</p>
          </div>
        </div>
        <span className="text-zinc-500 text-sm">Open →</span>
      </Link>

      <ReminderSettings />

      <PayPalAdminCard />

      {/* Achievements */}
      <section className="mt-12" data-testid="achievements-section">
        <p className="eyebrow">Achievements · Proof</p>
        <h2 className="font-display font-black text-2xl md:text-3xl mt-1">
          BADGES <span style={{ color: "#FF0055" }}>({achievements?.badges?.filter((b) => b.earned).length ?? 0}/{achievements?.badges?.length ?? 0})</span>
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mt-6">
          <div className="card" style={{ borderColor: (achievements?.current_streak ?? 0) > 0 ? "#FF0055" : "#27272A" }}>
            <p className="eyebrow flex items-center gap-1.5" style={{ color: "#FF0055" }}><Zap size={11} strokeWidth={2.5}/> Current streak</p>
            <p className="font-display font-black text-4xl mt-2">
              {achievements?.current_streak ?? 0}<span className="text-zinc-600 text-lg ml-2">days</span>
            </p>
          </div>
          <div className="card">
            <p className="eyebrow">Longest streak</p>
            <p className="font-display font-black text-4xl mt-2">
              {achievements?.longest_streak ?? 0}<span className="text-zinc-600 text-lg ml-2">days</span>
            </p>
          </div>
          <div className="card">
            <p className="eyebrow">Full days built</p>
            <p className="font-display font-black text-4xl mt-2">{achievements?.completed_days ?? 0}<span className="text-zinc-600 text-lg">/30</span></p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6 stagger">
          {achievements?.badges?.map((b) => <BadgeCard key={b.id} badge={b}/>)}
        </div>
      </section>

      {/* Journal */}
      <section className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="eyebrow">Record the proof</p>
            <h2 className="font-display font-black text-2xl md:text-3xl mt-1">JOURNAL</h2>
          </div>
          <button className="btn-secondary flex items-center gap-2" onClick={getPrompt} disabled={loading} data-testid="ai-prompt-btn">
            <Sparkles size={14}/> {loading ? "…" : "AI prompt"}
          </button>
        </div>
        {aiPrompt && (
          <div className="card mb-4" style={{ borderColor: "#FF0055" }}>
            <p className="eyebrow" style={{ color: "#FF0055" }}>Reflection prompt</p>
            <p className="font-display font-bold text-lg mt-2 uppercase leading-tight">{aiPrompt}</p>
          </div>
        )}
        <div className="space-y-3">
          {entries.length === 0 && (
            <div className="card text-zinc-500 text-sm">No entries yet. Record proof from Home or a day.</div>
          )}
          {entries.map((e) => (
            <div key={e.id} className="card">
              {e.prompt && <p className="eyebrow" style={{ color: "#FF0055" }}>{e.prompt}</p>}
              <p className="text-sm text-zinc-200 mt-2 whitespace-pre-wrap leading-relaxed">{e.content}</p>
              <div className="flex justify-between items-center mt-3">
                <p className="text-[10px] text-zinc-500 uppercase tracking-widest">
                  {e.date}{e.day ? ` · Day ${e.day}` : ""}
                </p>
                <button className="btn-ghost" onClick={() => delJournal(e.id)}><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
